import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api, ApiError } from '@/api/client'
import type {
  CertAttributeSpec,
  CertCategoryRow,
  CertJobFlag,
  CertRelationRow,
  CertRelationType,
  CertRequirementRow,
  CertScopeType,
  CertTypeInput,
  CertTypeRow,
  ExpiryRule,
  WageClassificationRow,
} from '@/api/types'
import { EXPIRY_RULE_LABEL, dayLabel, useCertAccess } from '@/lib/certs'

/**
 * Certification setup: requirements, types, categories, relations.
 *
 * Requirements lead, because they are the part NW Linings decides and the
 * part that makes the Problems counts move. None are seeded. The first one
 * created is the moment "Required certification missing" can start counting
 * people, so the add form says so.
 *
 * Who may change what comes from the server's capability list:
 * requirements are Office and up, types/categories/relations Admin, today.
 * Nothing is deleted. Types and categories are deactivated; relations and
 * requirements are closed and stay in the history.
 */

type Tab = 'requirements' | 'types' | 'categories' | 'relations'

const TABS: { key: Tab; label: string }[] = [
  { key: 'requirements', label: 'Requirements' },
  { key: 'types', label: 'Types' },
  { key: 'categories', label: 'Categories' },
  { key: 'relations', label: 'Relations' },
]

const SCOPE_LABEL: Record<CertScopeType, string> = {
  Company: 'All field staff',
  Classification: 'A classification',
  JobFlag: 'A kind of job',
  Customer: 'A customer',
  JobType: 'A job type (Sage UDF)',
  Job: 'One job',
}

const JOB_FLAGS: { key: CertJobFlag; label: string }[] = [
  { key: 'PrevailingWage', label: 'Prevailing wage jobs' },
  { key: 'DavisBacon', label: 'Davis-Bacon jobs' },
  { key: 'Union', label: 'Union jobs' },
  { key: 'PublicWorks', label: 'Public works jobs' },
  { key: 'CertifiedPayroll', label: 'Certified payroll jobs' },
  { key: 'OCIP', label: 'OCIP jobs' },
]

function errText(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message
  return err instanceof Error ? err.message : fallback
}

/**
 * `tabs` picks which parts this route shows: the Certifications menu opens
 * Requirements and Types on their own, and Categories & relations together.
 * With one tab there is no tab strip.
 */
export function CertSetupPage({ tabs = ['requirements', 'types', 'categories', 'relations'] }: { tabs?: Tab[] }) {
  const access = useCertAccess()
  const [params, setParams] = useSearchParams()
  const shown = TABS.filter((t) => tabs.includes(t.key))
  const tab = (shown.find((t) => t.key === params.get('tab'))?.key ?? shown[0].key) as Tab
  const heading = shown.length === 1 ? shown[0].label : shown.map((t) => t.label).join(' & ')

  const [types, setTypes] = useState<CertTypeRow[]>([])
  const [categories, setCategories] = useState<CertCategoryRow[]>([])
  const [relations, setRelations] = useState<CertRelationRow[]>([])
  const [requirements, setRequirements] = useState<CertRequirementRow[]>([])
  const [classes, setClasses] = useState<WageClassificationRow[]>([])
  const [showClosed, setShowClosed] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      const [t, c, r, q] = await Promise.all([
        api.certTypes(),
        api.certCategories(),
        api.certRelations(showClosed),
        api.certRequirements(showClosed),
      ])
      setTypes(t)
      setCategories(c)
      setRelations(r)
      setRequirements(q)
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 404
          ? 'Certifications are not switched on for your account.'
          : errText(err, 'Could not load setup'),
      )
    }
  }, [showClosed])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    void api.wageClassifications().then(setClasses).catch(() => setClasses([]))
  }, [])

  /** Run a write that returns a refreshed list; report the outcome. */
  async function save<T>(label: string, fn: () => Promise<T>, apply: (v: T) => void): Promise<boolean> {
    setError(null)
    setNotice(null)
    try {
      apply(await fn())
      setNotice(label)
      return true
    } catch (err) {
      setError(errText(err, 'Could not save. Nothing was changed.'))
      return false
    }
  }

  return (
    <div className="stack">
      {error && <div className="alert alert-error">{error}</div>}
      {notice && <div className="alert alert-ok">{notice}</div>}

      <div className="card">
        <h2 style={{ marginTop: 0 }}>{heading}</h2>
        {shown.length > 1 && (
          <div className="segmented" role="tablist" style={{ gridTemplateColumns: `repeat(${shown.length}, 1fr)` }}>
            {shown.map((t) => (
              <button
                key={t.key}
                role="tab"
                aria-selected={tab === t.key}
                onClick={() => setParams({ tab: t.key })}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}
        {(tab === 'requirements' || tab === 'relations') && (
          <label className="checkline" style={{ fontWeight: 400 }}>
            <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} />{' '}
            Show closed ones too
          </label>
        )}
      </div>

      {tab === 'requirements' && (
        <RequirementsTab
          rows={requirements}
          types={types}
          classes={classes}
          canEdit={access.can('manage_requirements')}
          onCreate={(payload) =>
            save('Requirement added.', () => api.createCertRequirement(payload), setRequirements)
          }
          onClose={(id) => save('Requirement closed.', () => api.closeCertRequirement(id), setRequirements)}
        />
      )}
      {tab === 'types' && (
        <TypesTab
          rows={types}
          categories={categories}
          canEdit={access.can('manage_types')}
          onCreate={(payload) => save('Type added.', () => api.createCertType(payload), setTypes)}
          onUpdate={(id, payload) => save('Type saved.', () => api.updateCertType(id, payload), setTypes)}
        />
      )}
      {tab === 'categories' && (
        <CategoriesTab
          rows={categories}
          canEdit={access.can('manage_types')}
          onCreate={(name, restricted) =>
            save('Category added.', () => api.createCertCategory({ name, is_restricted: restricted }), setCategories)
          }
          onUpdate={(id, payload) =>
            save('Category saved.', () => api.updateCertCategory(id, payload), setCategories)
          }
        />
      )}
      {tab === 'relations' && (
        <RelationsTab
          rows={relations}
          types={types}
          canEdit={access.can('manage_types')}
          onCreate={(payload) => save('Relation added.', () => api.createCertRelation(payload), setRelations)}
          onClose={(id) => save('Relation closed.', () => api.closeCertRelation(id), setRelations)}
        />
      )}
    </div>
  )
}

// --------------------------------------------------------------------------
// Requirements
// --------------------------------------------------------------------------

function RequirementsTab({
  rows,
  types,
  classes,
  canEdit,
  onCreate,
  onClose,
}: {
  rows: CertRequirementRow[]
  types: CertTypeRow[]
  classes: WageClassificationRow[]
  canEdit: boolean
  onCreate: (p: Parameters<typeof api.createCertRequirement>[0]) => Promise<boolean>
  onClose: (id: number) => Promise<boolean>
}) {
  const [typeId, setTypeId] = useState<number | ''>('')
  const [scope, setScope] = useState<CertScopeType>('Company')
  const [value, setValue] = useState('')
  const [grace, setGrace] = useState('0')
  const [from, setFrom] = useState('')
  const [note, setNote] = useState('')
  const open = rows.filter((r) => !r.effective_to)

  async function add() {
    if (typeId === '') return
    const ok = await onCreate({
      cert_type_id: typeId,
      scope_type: scope,
      scope_value: scope === 'Company' ? null : value.trim() || null,
      new_hire_grace_days: Number(grace) || 0,
      effective_from: from || null,
      note: note.trim() || null,
    })
    if (ok) {
      setValue('')
      setNote('')
    }
  }

  return (
    <>
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Requirements</h3>
        <p className="muted">
          Who must hold what. A company-wide rule applies to active field staff; a job rule applies to
          anyone recorded on a matching job this pay week or last. Requirements are NW Linings' call.
        </p>
        {rows.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>No requirements yet, so nobody is counted as missing anything.</p>
        ) : (
          <div className="table-wrap table-dense">
            <table>
              <thead>
                <tr>
                  <th>Certification</th>
                  <th>Applies to</th>
                  <th>New-hire grace</th>
                  <th>From</th>
                  <th>Note</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.cert_requirement_id} style={r.effective_to ? { opacity: 0.6 } : undefined}>
                    <td>{r.cert_type_name}</td>
                    <td>{r.scope_label}</td>
                    <td>{r.new_hire_grace_days ? `${r.new_hire_grace_days} days` : '—'}</td>
                    <td className="muted">
                      {dayLabel(r.effective_from)}
                      {r.effective_to && ` – closed ${dayLabel(r.effective_to)}`}
                    </td>
                    <td className="muted">{r.note}</td>
                    <td>
                      {canEdit && !r.effective_to && (
                        <button className="btn btn-secondary btn-sm" onClick={() => void onClose(r.cert_requirement_id)}>
                          Close
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {canEdit && (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Add a requirement</h3>
          {open.length === 0 && (
            <div className="alert alert-warn">
              This will be the first requirement. From the moment it is saved, everyone it applies to who
              has no card on file is counted on Problems as missing it.
            </div>
          )}
          <div className="field-grid">
            <div className="form-field">
              <label htmlFor="rqType">Certification</label>
              <select id="rqType" value={typeId} onChange={(e) => setTypeId(e.target.value ? Number(e.target.value) : '')}>
                <option value="">Choose…</option>
                {types.filter((t) => t.is_active).map((t) => (
                  <option key={t.cert_type_id} value={t.cert_type_id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label htmlFor="rqScope">Required of</label>
              <select
                id="rqScope"
                value={scope}
                onChange={(e) => {
                  setScope(e.target.value as CertScopeType)
                  setValue('')
                }}
              >
                {(Object.keys(SCOPE_LABEL) as CertScopeType[]).map((s) => (
                  <option key={s} value={s}>{SCOPE_LABEL[s]}</option>
                ))}
              </select>
            </div>
            {scope === 'Classification' && (
              <div className="form-field">
                <label htmlFor="rqValue">Classification</label>
                <select id="rqValue" value={value} onChange={(e) => setValue(e.target.value)}>
                  <option value="">Choose…</option>
                  {classes.map((c) => (
                    <option key={c.wage_classification_id} value={String(c.wage_classification_id)}>
                      {c.description} ({c.employee_count})
                    </option>
                  ))}
                </select>
              </div>
            )}
            {scope === 'JobFlag' && (
              <div className="form-field">
                <label htmlFor="rqValue">Kind of job</label>
                <select id="rqValue" value={value} onChange={(e) => setValue(e.target.value)}>
                  <option value="">Choose…</option>
                  {JOB_FLAGS.map((f) => (
                    <option key={f.key} value={f.key}>{f.label}</option>
                  ))}
                </select>
              </div>
            )}
            {(scope === 'Job' || scope === 'Customer' || scope === 'JobType') && (
              <div className="form-field">
                <label htmlFor="rqValue">
                  {scope === 'Job' ? 'Job number' : scope === 'Customer' ? 'Division-customer, as in Sage' : 'Job type, as written in Sage'}
                </label>
                <input
                  id="rqValue"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder={scope === 'Job' ? 'N25197' : scope === 'Customer' ? '00-ABC123' : 'Mine'}
                  autoCapitalize={scope === 'Job' ? 'characters' : undefined}
                />
              </div>
            )}
            <div className="form-field">
              <label htmlFor="rqGrace">New-hire grace (days)</label>
              <input id="rqGrace" type="number" min={0} max={365} value={grace} onChange={(e) => setGrace(e.target.value)} />
            </div>
            <div className="form-field">
              <label htmlFor="rqFrom">Starting (blank = today)</label>
              <input id="rqFrom" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="form-field">
              <label htmlFor="rqNote">Note</label>
              <input id="rqNote" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Micron site badge" />
            </div>
          </div>
          <button
            className="btn btn-sm"
            disabled={typeId === '' || (scope !== 'Company' && !value.trim())}
            onClick={() => void add()}
          >
            Add requirement
          </button>
        </div>
      )}
    </>
  )
}

// --------------------------------------------------------------------------
// Types
// --------------------------------------------------------------------------

interface TypeDraft {
  cert_category_id: number | ''
  name: string
  short_name: string
  issuing_body: string
  expiry_rule: ExpiryRule
  validity_months: string
  warn_days: string
  restricted: 'inherit' | 'yes' | 'no'
  description: string
  is_active: boolean
  attribute_spec: CertAttributeSpec[]
}

function draftOf(t?: CertTypeRow): TypeDraft {
  return {
    cert_category_id: t?.cert_category_id ?? '',
    name: t?.name ?? '',
    short_name: t?.short_name ?? '',
    issuing_body: t?.issuing_body ?? '',
    expiry_rule: t?.expiry_rule ?? 'FixedMonths',
    validity_months: t?.validity_months ? String(t.validity_months) : '',
    warn_days: String(t?.warn_days ?? 30),
    restricted: t?.is_restricted_override == null ? 'inherit' : t.is_restricted_override ? 'yes' : 'no',
    description: t?.description ?? '',
    is_active: t?.is_active ?? true,
    attribute_spec: t?.attribute_spec ?? [],
  }
}

function payloadOf(d: TypeDraft): CertTypeInput {
  return {
    cert_category_id: d.cert_category_id === '' ? undefined : d.cert_category_id,
    name: d.name.trim(),
    short_name: d.short_name.trim(),
    issuing_body: d.issuing_body.trim() || null,
    expiry_rule: d.expiry_rule,
    validity_months: d.expiry_rule === 'FixedMonths' ? Number(d.validity_months) || null : null,
    warn_days: Number(d.warn_days) || 0,
    is_restricted_override: d.restricted === 'inherit' ? null : d.restricted === 'yes',
    description: d.description.trim() || null,
    is_active: d.is_active,
    attribute_spec: d.attribute_spec.filter((s) => s.key.trim() && s.label.trim()),
  }
}

function TypesTab({
  rows,
  categories,
  canEdit,
  onCreate,
  onUpdate,
}: {
  rows: CertTypeRow[]
  categories: CertCategoryRow[]
  canEdit: boolean
  onCreate: (p: CertTypeInput) => Promise<boolean>
  onUpdate: (id: number, p: CertTypeInput) => Promise<boolean>
}) {
  const [editing, setEditing] = useState<number | 'new' | null>(null)
  const grouped = useMemo(() => {
    const m = new Map<string, CertTypeRow[]>()
    for (const t of rows) m.set(t.category_name, [...(m.get(t.category_name) ?? []), t])
    return [...m.entries()]
  }, [rows])

  return (
    <div className="card">
      <div className="crew-heading">
        <h3 style={{ margin: 0 }}>Types</h3>
        {canEdit && editing === null && (
          <button className="btn btn-sm" onClick={() => setEditing('new')}>Add a type</button>
        )}
      </div>
      <p className="muted">
        Validity periods were seeded as common defaults and are for NW Linings to confirm. Changing a
        period does not rewrite cards already on file.
      </p>
      {editing === 'new' && (
        <TypeEditor
          categories={categories}
          initial={draftOf()}
          onCancel={() => setEditing(null)}
          onSave={async (d) => {
            if (await onCreate(payloadOf(d))) setEditing(null)
          }}
        />
      )}
      {grouped.map(([cat, list]) => (
        <div key={cat} style={{ marginTop: 12 }}>
          <h4 style={{ margin: '0 0 4px' }}>{cat}</h4>
          <div className="table-wrap table-dense">
            <table>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Short</th>
                  <th>Expiry</th>
                  <th>Warn</th>
                  <th>Extra fields</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {list.map((t) =>
                  editing === t.cert_type_id ? (
                    <tr key={t.cert_type_id}>
                      <td colSpan={6}>
                        <TypeEditor
                          categories={categories}
                          initial={draftOf(t)}
                          onCancel={() => setEditing(null)}
                          onSave={async (d) => {
                            if (await onUpdate(t.cert_type_id, payloadOf(d))) setEditing(null)
                          }}
                        />
                      </td>
                    </tr>
                  ) : (
                    <tr key={t.cert_type_id} style={t.is_active ? undefined : { opacity: 0.6 }}>
                      <td>
                        {t.name}
                        {t.is_restricted && <span className="badge" style={{ marginLeft: 6 }}>restricted</span>}
                        {!t.is_active && <span className="badge" style={{ marginLeft: 6 }}>inactive</span>}
                        {t.description && <div className="muted" style={{ fontSize: '0.75rem' }}>{t.description}</div>}
                      </td>
                      <td className="mono">{t.short_name}</td>
                      <td>
                        {t.expiry_rule === 'FixedMonths' ? `${t.validity_months} months` : EXPIRY_RULE_LABEL[t.expiry_rule]}
                      </td>
                      <td>{t.expiry_rule === 'None' ? '—' : `${t.warn_days} days`}</td>
                      <td className="muted">{t.attribute_spec.map((s) => s.label).join(', ') || '—'}</td>
                      <td>
                        {canEdit && editing === null && (
                          <button className="btn btn-secondary btn-sm" onClick={() => setEditing(t.cert_type_id)}>
                            Edit
                          </button>
                        )}
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  )
}

function TypeEditor({
  categories,
  initial,
  onSave,
  onCancel,
}: {
  categories: CertCategoryRow[]
  initial: TypeDraft
  onSave: (d: TypeDraft) => Promise<void>
  onCancel: () => void
}) {
  const [d, setD] = useState<TypeDraft>(initial)
  const [busy, setBusy] = useState(false)
  const set = <K extends keyof TypeDraft>(k: K, v: TypeDraft[K]) => setD((x) => ({ ...x, [k]: v }))

  return (
    <div className="card" style={{ background: 'var(--c-surface-alt)', margin: '0.5rem 0' }}>
      <div className="field-grid">
        <div className="form-field">
          <label htmlFor="tyName">Name</label>
          <input id="tyName" value={d.name} onChange={(e) => set('name', e.target.value)} maxLength={80} />
        </div>
        <div className="form-field">
          <label htmlFor="tyShort">Short name (matrix heading)</label>
          <input id="tyShort" value={d.short_name} onChange={(e) => set('short_name', e.target.value)} maxLength={20} />
        </div>
        <div className="form-field">
          <label htmlFor="tyCat">Category</label>
          <select
            id="tyCat"
            value={d.cert_category_id}
            onChange={(e) => set('cert_category_id', e.target.value ? Number(e.target.value) : '')}
          >
            <option value="">Choose…</option>
            {categories.map((c) => (
              <option key={c.cert_category_id} value={c.cert_category_id}>
                {c.name}{c.is_restricted ? ' (restricted)' : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="form-field">
          <label htmlFor="tyRule">Expiry</label>
          <select id="tyRule" value={d.expiry_rule} onChange={(e) => set('expiry_rule', e.target.value as ExpiryRule)}>
            {(Object.keys(EXPIRY_RULE_LABEL) as ExpiryRule[]).map((r) => (
              <option key={r} value={r}>{EXPIRY_RULE_LABEL[r]}</option>
            ))}
          </select>
        </div>
        {d.expiry_rule === 'FixedMonths' && (
          <div className="form-field">
            <label htmlFor="tyMonths">Lasts (months)</label>
            <input id="tyMonths" type="number" min={1} max={600} value={d.validity_months}
              onChange={(e) => set('validity_months', e.target.value)} />
          </div>
        )}
        {d.expiry_rule !== 'None' && (
          <div className="form-field">
            <label htmlFor="tyWarn">Warn this many days ahead</label>
            <input id="tyWarn" type="number" min={0} max={365} value={d.warn_days}
              onChange={(e) => set('warn_days', e.target.value)} />
          </div>
        )}
        <div className="form-field">
          <label htmlFor="tyRestricted">Restricted (medical)</label>
          <select id="tyRestricted" value={d.restricted}
            onChange={(e) => set('restricted', e.target.value as TypeDraft['restricted'])}>
            <option value="inherit">Same as its category</option>
            <option value="yes">Yes — Admin only sees detail</option>
            <option value="no">No</option>
          </select>
        </div>
        <div className="form-field">
          <label htmlFor="tyBody">Issuing body</label>
          <input id="tyBody" value={d.issuing_body} onChange={(e) => set('issuing_body', e.target.value)} maxLength={80} />
        </div>
        <div className="form-field">
          <label htmlFor="tyDesc">Description</label>
          <input id="tyDesc" value={d.description} onChange={(e) => set('description', e.target.value)} maxLength={400} />
        </div>
        <div className="form-field">
          <label className="checkline" style={{ fontWeight: 400 }}>
            <input type="checkbox" checked={d.is_active} onChange={(e) => set('is_active', e.target.checked)} />{' '}
            Offered for new records
          </label>
        </div>
      </div>

      <h5 style={{ margin: '0.75rem 0 0.25rem' }}>Extra fields this type records</h5>
      {d.attribute_spec.map((s, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
          <input
            aria-label="Key"
            className="mono"
            style={{ width: 160 }}
            value={s.key}
            placeholder="key_name"
            onChange={(e) => {
              const v = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_')
              set('attribute_spec', d.attribute_spec.map((x, j) => (j === i ? { ...x, key: v } : x)))
            }}
          />
          <input
            aria-label="Label"
            style={{ flex: 1 }}
            value={s.label}
            placeholder="Label shown on the form"
            onChange={(e) =>
              set('attribute_spec', d.attribute_spec.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))
            }
          />
          <label className="checkline" style={{ fontWeight: 400 }}>
            <input
              type="checkbox"
              checked={s.required}
              onChange={(e) =>
                set('attribute_spec', d.attribute_spec.map((x, j) => (j === i ? { ...x, required: e.target.checked } : x)))
              }
            />{' '}
            required
          </label>
          <button className="btn btn-plain btn-sm"
            onClick={() => set('attribute_spec', d.attribute_spec.filter((_, j) => j !== i))}>
            Remove
          </button>
        </div>
      ))}
      <button
        className="btn btn-secondary btn-sm"
        onClick={() => set('attribute_spec', [...d.attribute_spec, { key: '', label: '', required: false }])}
      >
        Add a field
      </button>

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button
          className="btn btn-sm"
          disabled={busy || !d.name.trim() || !d.short_name.trim() || d.cert_category_id === ''
            || (d.expiry_rule === 'FixedMonths' && !Number(d.validity_months))}
          onClick={async () => {
            setBusy(true)
            try {
              await onSave(d)
            } finally {
              setBusy(false)
            }
          }}
        >
          {busy ? 'Saving…' : 'Save type'}
        </button>
        <button className="btn btn-secondary btn-sm" onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
    </div>
  )
}

// --------------------------------------------------------------------------
// Categories
// --------------------------------------------------------------------------

function CategoriesTab({
  rows,
  canEdit,
  onCreate,
  onUpdate,
}: {
  rows: CertCategoryRow[]
  canEdit: boolean
  onCreate: (name: string, restricted: boolean) => Promise<boolean>
  onUpdate: (id: number, p: { name?: string; is_restricted?: boolean; is_active?: boolean; sort_order?: number }) => Promise<boolean>
}) {
  const [name, setName] = useState('')
  const [restricted, setRestricted] = useState(false)

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Categories</h3>
      <p className="muted">
        A restricted category hides the detail of every type in it from everyone but Admins — only the
        outcome shows. A type can override its category.
      </p>
      <div className="table-wrap table-dense">
        <table>
          <thead>
            <tr>
              <th>Category</th>
              <th>Restricted</th>
              <th>Order</th>
              <th>Active</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.cert_category_id}>
                <td>{c.name}</td>
                <td>
                  {canEdit ? (
                    <input
                      type="checkbox"
                      aria-label={`${c.name} restricted`}
                      checked={c.is_restricted}
                      onChange={(e) => void onUpdate(c.cert_category_id, { is_restricted: e.target.checked })}
                    />
                  ) : c.is_restricted ? 'Yes' : '—'}
                </td>
                <td>{c.sort_order}</td>
                <td>
                  {canEdit ? (
                    <input
                      type="checkbox"
                      aria-label={`${c.name} active`}
                      checked={c.is_active}
                      onChange={(e) => void onUpdate(c.cert_category_id, { is_active: e.target.checked })}
                    />
                  ) : c.is_active ? 'Yes' : 'No'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {canEdit && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'end', marginTop: 12, flexWrap: 'wrap' }}>
          <div className="form-field" style={{ margin: 0 }}>
            <label htmlFor="catName">New category</label>
            <input id="catName" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} />
          </div>
          <label className="checkline" style={{ fontWeight: 400 }}>
            <input type="checkbox" checked={restricted} onChange={(e) => setRestricted(e.target.checked)} /> Restricted
          </label>
          <button
            className="btn btn-sm"
            disabled={!name.trim()}
            onClick={async () => {
              if (await onCreate(name.trim(), restricted)) {
                setName('')
                setRestricted(false)
              }
            }}
          >
            Add category
          </button>
        </div>
      )}
    </div>
  )
}

// --------------------------------------------------------------------------
// Relations
// --------------------------------------------------------------------------

function RelationsTab({
  rows,
  types,
  canEdit,
  onCreate,
  onClose,
}: {
  rows: CertRelationRow[]
  types: CertTypeRow[]
  canEdit: boolean
  onCreate: (p: { cert_type_id: number; related_cert_type_id: number; relation_type: CertRelationType; note?: string | null }) => Promise<boolean>
  onClose: (id: number) => Promise<boolean>
}) {
  const [a, setA] = useState<number | ''>('')
  const [b, setB] = useState<number | ''>('')
  const [kind, setKind] = useState<CertRelationType>('Prerequisite')
  const [note, setNote] = useState('')

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Relations</h3>
      <p className="muted">
        <strong>Requires</strong>: the first is blocked unless the second is current and was in place when
        the first was issued (a fit test needs medical clearance first). <strong>Counts as</strong>: holding
        the first meets a requirement for the second (OSHA 30 counts as OSHA 10). One level only.
      </p>
      <div className="table-wrap table-dense">
        <table>
          <thead>
            <tr>
              <th>Certification</th>
              <th></th>
              <th>Certification</th>
              <th>Note</th>
              <th>Since</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.cert_type_relation_id} style={r.effective_to ? { opacity: 0.6 } : undefined}>
                <td>{r.cert_type_name}</td>
                <td className="muted">{r.relation_type === 'Prerequisite' ? 'requires' : 'counts as'}</td>
                <td>{r.related_cert_type_name}</td>
                <td className="muted">{r.note}</td>
                <td className="muted">
                  {r.created_by ? dayLabel(r.effective_from) : 'Seeded'}
                  {r.effective_to && ` – closed ${dayLabel(r.effective_to)}`}
                </td>
                <td>
                  {canEdit && !r.effective_to && (
                    <button className="btn btn-secondary btn-sm" onClick={() => void onClose(r.cert_type_relation_id)}>
                      Close
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {canEdit && (
        <div className="field-grid" style={{ marginTop: 12 }}>
          <div className="form-field">
            <label htmlFor="rlA">Certification</label>
            <select id="rlA" value={a} onChange={(e) => setA(e.target.value ? Number(e.target.value) : '')}>
              <option value="">Choose…</option>
              {types.map((t) => <option key={t.cert_type_id} value={t.cert_type_id}>{t.name}</option>)}
            </select>
          </div>
          <div className="form-field">
            <label htmlFor="rlKind">Relation</label>
            <select id="rlKind" value={kind} onChange={(e) => setKind(e.target.value as CertRelationType)}>
              <option value="Prerequisite">requires</option>
              <option value="Satisfies">counts as</option>
            </select>
          </div>
          <div className="form-field">
            <label htmlFor="rlB">Certification</label>
            <select id="rlB" value={b} onChange={(e) => setB(e.target.value ? Number(e.target.value) : '')}>
              <option value="">Choose…</option>
              {types.filter((t) => t.cert_type_id !== a).map((t) => (
                <option key={t.cert_type_id} value={t.cert_type_id}>{t.name}</option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label htmlFor="rlNote">Note (e.g. the OSHA citation)</label>
            <input id="rlNote" value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} />
          </div>
          <div className="form-field">
            <button
              className="btn btn-sm"
              disabled={a === '' || b === ''}
              onClick={async () => {
                if (a === '' || b === '') return
                if (await onCreate({ cert_type_id: a, related_cert_type_id: b, relation_type: kind, note: note.trim() || null })) {
                  setA('')
                  setB('')
                  setNote('')
                }
              }}
            >
              Add relation
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
